/**
 * Nmbrs Integration Schemas
 *
 * Zod validation schemas for Nmbrs API endpoints.
 * Story 1.2: Medewerker Koppeling & Ontkoppeling
 */
import { z } from 'zod';
export const linkEmployeeSchema = z.object({
    employeeId: z.number().int().positive('Employee ID must be a positive integer'),
    nmbrsEmployeeId: z.number().int().positive('Nmbrs Employee ID must be a positive integer'),
});
/**
 * Nmbrs Backfill Epic — shared request schema for the three bulk admin
 * actions (/link/bulk, /leave/backfill, /schedule/backfill). All three
 * accept the same base `apply` discriminator (default false = dry-run).
 */
export const bulkApplySchema = z.object({
    apply: z.boolean().optional().default(false),
});
/**
 * Story 1.2 (CAP-2): leave backfill — optional explicit year window that
 * overrides the per-employee defaults (startdatum-derived, MAX_LOOKBACK_YEARS
 * lower bound) for the whole batch.
 */
export const leaveBackfillSchema = z.object({
    apply: z.boolean().optional().default(false),
    fromYear: z.number().int().min(1900).max(2200).optional(),
    toYear: z.number().int().min(1900).max(2200).optional(),
}).refine((data) => data.fromYear === undefined || data.toYear === undefined || data.toYear >= data.fromYear, { message: 'toYear moet op of na fromYear liggen', path: ['toYear'] });
/** `YYYY-MM-DD` shape check, same style as `leaveRequestSchema.startDate`. */
const DATE_ONLY_REGEX = /^\d{4}-\d{2}-\d{2}$/;
/**
 * Epic nmbrs-verzuim-kalender, Story 1.1 (CAP-1): verzuim (absence)
 * backfill — same `apply`-discriminator style as `bulkApplySchema`/
 * `leaveBackfillSchema`, plus an optional `sinceDate` override (default
 * `'2026-01-01'` applied at the route/service layer, not here — this
 * schema only validates the shape when a caller supplies one).
 *
 * The `.refine` catches shape-valid-but-impossible values like month `13`
 * (`new Date('2026-13-01')` → `NaN`), but — same accepted limitation as
 * `leaveRequestSchema.startDate`/`endDate` above and
 * `nmbrsLeaveBackfillService.toDateOnly`'s doc-comment — does NOT catch
 * day-of-month overflow within a valid month (e.g. `'2026-02-30'`, which
 * `Date` silently rolls forward rather than rejecting). A cheap sanity
 * check, not full calendar validation; consistent with every other
 * date-only field in this codebase.
 */
export const absenceBackfillSchema = z.object({
    apply: z.boolean().optional().default(false),
    sinceDate: z
        .string()
        .regex(DATE_ONLY_REGEX, 'sinceDate moet het formaat YYYY-MM-DD hebben')
        .refine((d) => !isNaN(new Date(d).getTime()), 'sinceDate is geen geldige datum')
        .optional(),
});
/**
 * Story 3.1: Leave balance query validation
 */
export const leaveBalanceQuerySchema = z.object({
    year: z.coerce.number().int().min(2020).max(2099).optional(),
});
/**
 * Story 3.1: Employee ID parameter validation
 */
export const employeeIdParamSchema = z.object({
    employeeId: z.coerce.number().int().positive(),
});
/**
 * Story 3.2: Leave list query validation
 */
export const leaveListQuerySchema = z.object({
    year: z.coerce.number().int().min(2020).max(2099).optional(),
});
/**
 * Story 3.3: Leave request submission schema
 */
export const leaveRequestSchema = z.object({
    employeeId: z.number().int().positive(),
    leaveType: z.number().int().min(1).max(4, 'Ongeldig verloftype (1-4)'),
    startDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Ongeldig datumformaat').refine((d) => !isNaN(new Date(d).getTime()), 'Ongeldige datum'),
    endDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Ongeldig datumformaat').refine((d) => !isNaN(new Date(d).getTime()), 'Ongeldige datum'),
    hours: z.number().positive('Uren moeten positief zijn').max(744, 'Maximum 744 uren per aanvraag'),
    description: z.string().max(500).optional(),
}).refine((data) => data.endDate >= data.startDate, { message: 'Einddatum moet op of na startdatum liggen', path: ['endDate'] });
/**
 * Story 3.3: Request ID parameter schema
 */
export const requestIdParamSchema = z.object({
    requestId: z.coerce.number().int().positive(),
});
/**
 * Story 3.4: Team leave calendar query validation
 */
export const teamLeaveQuerySchema = z.object({
    startDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Ongeldig datumformaat'),
    endDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Ongeldig datumformaat'),
}).refine((data) => data.endDate >= data.startDate, { message: 'Einddatum moet op of na startdatum liggen' });
/**
 * Story 3.5: Absence employee ID parameter validation
 * Re-uses employeeIdParamSchema — declared here for documentation clarity.
 */
export const absenceEmployeeIdParamSchema = employeeIdParamSchema;
/**
 * Story 2.2: Sync Log Query
 */
export const syncLogQuerySchema = z.object({
    page: z.coerce.number().int().positive().default(1),
    limit: z.coerce.number().int().min(1).max(100).default(20),
});
/**
 * Story 2.3: Override Management schemas
 */
export const ALLOWED_OVERRIDE_FIELDS = ['name', 'email', 'functie', 'afdeling', 'startdatum'];
export const overrideParamsSchema = z.object({
    employeeId: z.coerce.number().int().positive('Employee ID must be a positive integer'),
});
export const setOverrideSchema = z.object({
    fieldName: z.enum(ALLOWED_OVERRIDE_FIELDS, {
        errorMap: () => ({ message: `Field must be one of: ${ALLOWED_OVERRIDE_FIELDS.join(', ')}` }),
    }),
    overrideValue: z.string().min(1, 'Override value cannot be empty').max(500),
});
export const removeOverrideParamsSchema = z.object({
    employeeId: z.coerce.number().int().positive('Employee ID must be a positive integer'),
    fieldName: z.enum(ALLOWED_OVERRIDE_FIELDS, {
        errorMap: () => ({ message: `Field must be one of: ${ALLOWED_OVERRIDE_FIELDS.join(', ')}` }),
    }),
});
//# sourceMappingURL=nmbrs.js.map